// https://developers.cloudflare.com/turnstile/get-started/client-side-rendering/
import type { Turnstile } from '~/types'

declare global {
  interface Window {
    turnstile?: Turnstile
  }
}

const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

let scriptLoadPromise: Promise<void> | null = null

function loadTurnstileScript (): Promise<void> {
  if (window.turnstile) return Promise.resolve()
  if (scriptLoadPromise) return scriptLoadPromise

  scriptLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = TURNSTILE_SCRIPT_SRC
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load Cloudflare Turnstile'))
    document.head.appendChild(script)
  })

  const baseClasses = `
    .opacity-0 { opacity: 0 }
    .opacity-1 { opacity: 1 }
    .pointer-events-none {
      pointer-events: none;
    }
  `
  const styleElement = document.createElement('style')
  styleElement.innerHTML = baseClasses
  document.body.appendChild(styleElement)

  return scriptLoadPromise
}

export type TurnstileCaptchaOptions = {
  theme?: 'light' | 'dark' | 'auto'
}

export type TurnstileCaptchaController = {
  // Starts (or restarts) a verification run. With appearance: 'interaction-only',
  // Cloudflare only inserts visible widget content into the container when it
  // actually needs the user to solve a challenge - the modal shell mirrors that.
  execute: () => void
  // Clears the current run/token and hides the modal shell. Safe to call between runs.
  reset: () => void
  // Last resolved token for this widget, or null if none/consumed by reset().
  getToken: () => string | null
  // Overwrites the success handler - callable again for a subsequent run.
  onSuccess: (callback: (token: string) => void) => void
  // Overwrites the failure handler (covers error/expired/timeout) - callable again for a subsequent run.
  onError: (callback: (error: Error) => void) => void
  // Tears down the widget and removes the modal shell from the DOM.
  destroy: () => void
}

const overlayClassName = `
  opacity-0 pointer-events-none
` as string

const overlayStyle = `
  position: fixed;

  inset: 0;

  z-index: 2005;

  display: flex;
  align-items: center;
  justify-content: center;

  background-color: rgba(0, 0, 0, 0.4);

  backdrop-filter: blur(4px);

  transition-property: opacity;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;

` as string

// bg-bg-primary-alt border border-border-secondary rounded-xl p-6
const cardClassName = `
` as string

const cardStyle = `
` as string

// With appearance: 'interaction-only', Cloudflare only makes the widget visibly take
// up space when it actually needs the user to solve a challenge - for a silent pass
// it stays 0x0. That resize is a reliable "show" signal, but only if the element we
// measure has no styling of our own contributing to its size (our card chrome - bg,
// border, padding - lives on a separate wrapper around `box`, which itself passed to
// render() bare, so its rendered size is 0x0 exactly when Cloudflare has nothing to
// show). It's only watched while a run is in-flight (see execute()/reset() below):
// Cloudflare doesn't necessarily collapse it back to 0x0 on success/error itself, so
// if we stayed subscribed permanently, reset()'s own DOM churn could re-trigger the
// "show" branch right after we've explicitly hidden the modal.
export async function createTurnstileCaptcha (siteKey: string, options?: TurnstileCaptchaOptions): Promise<TurnstileCaptchaController> {
  if (process.server) {
    throw new Error('Should only be loaded on the client-side')
  }

  await loadTurnstileScript()

  if (!window.turnstile) {
    throw new Error('Failed to load Cloudflare Turnstile')
  }

  const overlay = document.createElement('div')
  // @ts-ignore
  overlay.style = overlayStyle
  overlay.className = overlayClassName

  const card = document.createElement('div')
  // @ts-ignore
  card.style = cardStyle
  card.className = cardClassName
  overlay.appendChild(card)

  // Passed to render() as-is (no size/spacing of our own), so its rendered size
  // purely reflects whatever Cloudflare puts inside it.
  const box = document.createElement('div')
  card.appendChild(box)

  document.body.appendChild(overlay)

  let token: string | null = null
  let successCallback: ((token: string) => void) | null = null
  let errorCallback: ((error: Error) => void) | null = null

  function setOverlayVisible (visible: boolean) {
    overlay.classList.toggle('opacity-0', !visible)
    overlay.classList.toggle('pointer-events-none', !visible)
  }

  const resizeObserver = new ResizeObserver(() => {
    const { width, height } = box.getBoundingClientRect()
    setOverlayVisible(width > 0 && height > 0)
  })

  const widgetId = window.turnstile.render(box, {
    sitekey: siteKey,
    size: 'normal',
    execution: 'execute',
    retry: 'never',
    theme: options?.theme || 'auto',
    appearance: 'interaction-only',
    callback: (newToken) => {
      token = newToken
      successCallback?.(newToken)
    },
    'error-callback': (error) => {
      errorCallback?.(new Error(`Turnstile error: ${typeof error === 'string' ? error : JSON.stringify(error)}`))
      return true // do not let the widget auto-retry, callers control re-triggering
    },
    'expired-callback': () => {
      errorCallback?.(new Error('Turnstile token expired'))
    },
    'timeout-callback': () => {
      errorCallback?.(new Error('Turnstile challenge timed out'))
    },
  })

  if (!widgetId) {
    throw new Error('Failed to render Turnstile widget')
  }

  return {
    execute () {
      token = null
      resizeObserver.observe(box)
      window.turnstile?.execute(box)
    },
    reset () {
      resizeObserver.disconnect()
      setOverlayVisible(false)
      window.turnstile?.reset(widgetId)
      token = null
    },
    getToken () {
      return token
    },
    onSuccess (callback) {
      successCallback = callback
    },
    onError (callback) {
      errorCallback = callback
    },
    destroy () {
      resizeObserver.disconnect()
      window.turnstile?.remove(widgetId)
      overlay.remove()
    },
  }
}
